import { useState } from 'react';
import type { Document, Section } from '../types';
import { people, project } from '../data';
import { ChevronDown, DocIcon, Mark, MoreIcon, Search } from './DocIcons';

interface Props {
  docs: Document[];
  activeDocId: string;
  activeSectionId: string | null;
  activeSections: Section[];
  onPickDoc: (id: string) => void;
  onPickSection: (id: string) => void;
}

function StatusDot({ status }: { status: string }) {
  const bg =
    status === 'empty' ? 'var(--faint)' :
    status === 'generating' ? 'var(--ai)' : 'var(--good)';
  return <span style={{ width: 5, height: 5, borderRadius: 2, flexShrink: 0, background: bg, display: 'inline-block' }} />;
}


function SectionTree({ sections, activeId, onPick }: { sections: Section[]; activeId: string | null; onPick: (id: string) => void }) {
  return (
    <div style={{ marginTop: 2 }}>
      {sections.map((s) => (
        <div key={s.id}>
          <button
            onClick={() => onPick(s.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left',
              height: 'var(--row-h)', padding: '0 8px 0 23px', border: 'none',
              borderRadius: 'var(--r-sm)', flexShrink: 0, cursor: 'pointer',
              background: activeId === s.id ? 'var(--blue-soft)' : 'transparent',
              color: activeId === s.id ? 'var(--blue-ink)' : 'var(--ink-2)',
              fontWeight: activeId === s.id ? 600 : 500, fontSize: 13, fontFamily: 'var(--sans)',
            }}
          >
            <StatusDot status={s.status} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</span>
            {s.status === 'empty' && (
              <span className="mono" style={{ fontSize: 9, color: 'var(--faint)' }}>empty</span>
            )}
          </button>
          {s.children?.map((c) => (
            <button
              key={c.id}
              onClick={() => onPick(c.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left',
                height: 'var(--row-h)', padding: '0 8px 0 38px', border: 'none',
                borderRadius: 'var(--r-sm)', flexShrink: 0, cursor: 'pointer',
                background: activeId === c.id ? 'var(--blue-soft)' : 'transparent',
                color: activeId === c.id ? 'var(--blue-ink)' : 'var(--ink-2)',
                fontWeight: activeId === c.id ? 600 : 500, fontSize: 12, fontFamily: 'var(--sans)',
              }}
            >
              <span style={{ width: 4, height: 4, borderRadius: 2, flexShrink: 0, background: c.status === 'empty' ? 'var(--faint)' : 'var(--good)', display: 'inline-block' }} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

const CATEGORIES = ['Discovery', 'Definition', 'Architecture'];

export function DocSidebar({ docs, activeDocId, activeSectionId, activeSections, onPickDoc, onPickSection }: Props) {
  const [open, setOpen] = useState<Record<string, boolean>>({ Discovery: true, Definition: true, Architecture: true });
  const me = people.ad;

  return (
    <aside style={{
      borderRight: '1px solid var(--line)', background: 'var(--bg)',
      display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden',
    }}>
      {/* brand + project */}
      <div style={{ padding: '13px 13px 11px', borderBottom: '1px solid var(--line)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <Mark size={26} />
          <div style={{ lineHeight: 1.15, flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 14.5, letterSpacing: '-0.01em' }}>Vibelin</div>
            <div className="mono" style={{ fontSize: 9.5, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Console</div>
          </div>
          <button className="doc-icon-btn" style={{ width: 26, height: 26 }}><MoreIcon size={16} /></button>
        </div>
        <button style={{
          marginTop: 11, width: '100%', display: 'flex', alignItems: 'center', gap: 8, height: 38,
          padding: '0 10px', border: '1px solid var(--line)', background: 'var(--surface)',
          borderRadius: 'var(--r)', boxShadow: 'var(--shadow-sm)', cursor: 'pointer', fontFamily: 'var(--sans)',
        }}>
          <span style={{
            width: 22, height: 22, borderRadius: 6, background: 'var(--ink)', color: 'white',
            display: 'grid', placeItems: 'center', fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700,
          }}>H</span>
          <div style={{ flex: 1, textAlign: 'left', lineHeight: 1.2, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.name}</div>
            <div className="mono" style={{ fontSize: 9, color: 'var(--muted)' }}>{project.code} · {project.env}</div>
          </div>
          <ChevronDown size={15} style={{ color: 'var(--muted)' }} />
        </button>
      </div>

      {/* search */}
      <div style={{ padding: '10px 13px 6px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 7, height: 32, padding: '0 9px',
          border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)', color: 'var(--muted)',
        }}>
          <Search size={14} />
          <span style={{ fontSize: 12.5 }}>Search documents…</span>
          <span className="mono" style={{ marginLeft: 'auto', fontSize: 10, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 4, padding: '1px 5px' }}>⌘K</span>
        </div>
      </div>

      {/* document tree */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 9px 16px' }}>
        {CATEGORIES.map((cat) => {
          const list = docs.filter((d) => d.category === cat);
          if (!list.length) return null;
          return (
            <div key={cat} style={{ marginTop: 8 }}>
              <button
                onClick={() => setOpen((o) => ({ ...o, [cat]: !o[cat] }))}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, width: '100%', border: 'none',
                  background: 'transparent', padding: '4px 6px', color: 'var(--muted)', cursor: 'pointer', fontFamily: 'var(--sans)',
                }}
              >
                <ChevronDown size={13} style={{ transform: open[cat] ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                <span className="mono" style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{cat}</span>
                <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--faint)', fontWeight: 600 }}>{list.length}</span>
              </button>
              {open[cat] && list.map((d) => {
                const isActive = d.id === activeDocId;
                return (
                  <div key={d.id}>
                    <button
                      onClick={() => onPickDoc(d.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left',
                        height: 'var(--row-h)', padding: '0 8px', border: 'none',
                        borderRadius: 'var(--r-sm)', flexShrink: 0,
                        background: isActive ? 'var(--blue-soft)' : 'transparent',
                        color: isActive ? 'var(--blue-ink)' : 'var(--ink-2)',
                        fontWeight: 700, fontSize: 13, fontFamily: 'var(--sans)', cursor: 'pointer',
                      }}
                    >
                      <DocIcon size={15} style={{ color: isActive ? 'var(--blue-ink)' : 'var(--faint)' }} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</span>
                      <span className="mono" style={{ fontSize: 9.5, color: isActive ? 'var(--blue-ink)' : 'var(--faint)', opacity: isActive ? 1 : 0.7 }}>{d.code}</span>
                    </button>
                    {isActive && activeSections.length > 0 && (
                      <SectionTree sections={activeSections} activeId={activeSectionId} onPick={onPickSection} />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* user footer */}
      <div style={{ borderTop: '1px solid var(--line)', padding: '9px 13px', display: 'flex', alignItems: 'center', gap: 9 }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
          display: 'grid', placeItems: 'center', fontSize: 10.5, fontWeight: 700,
          fontFamily: 'var(--mono)', color: 'white', letterSpacing: '-0.02em',
          background: `oklch(0.6 0.14 ${me.hue})`,
        }}>{me.initials}</div>
        <div style={{ flex: 1, lineHeight: 1.2, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 12.5 }}>{me.name}</div>
          <div className="mono" style={{ fontSize: 9.5, color: 'var(--muted)' }}>{me.role}</div>
        </div>
        <button className="doc-icon-btn" style={{ width: 26, height: 26 }}><MoreIcon size={16} /></button>
      </div>
    </aside>
  );
}
